import React, { HTMLAttributes } from "react";
import { Money } from "@/helpers/Money";
import { Cadence } from "../portal/types";
import { AutorenewOutlined, LocalShippingOutlined } from "@mui/icons-material";
import { ProductCard, ProductCardProduct } from "../product/ProductCard";
import { productSelect } from "@/store";
import { cn } from "@/helpers/utils";
import theme from "@/theme";
import { useMediaQuery } from "@mui/material";
import { PriceDisplay } from "@/features/product/PriceDisplay";

export type ButtonAction = {
  displayText: string;
  href?: string;
  action?: () => void;
};

export type DrawerItemCardProps = {
  product: ProductCardProduct;
  price: Money;
  quantity: number;
  shippingInfo?: Cadence;
  loading?: boolean;
  disabled?: boolean;
  responsive?: boolean;
  isSubBadge?: boolean;
  scentBg?: "white" | "gray";
  orderItemCardActions?: React.ReactNode;
} & HTMLAttributes<HTMLElement>;

export const DrawerItemCard = ({
  product,
  price,
  shippingInfo,
  quantity,
  loading,
  disabled,
  responsive,
  scentBg,
  isSubBadge,
  orderItemCardActions,
  ...attrs
}: DrawerItemCardProps) => {
  const combinedFrequency = `${
    shippingInfo?.frequency
  } ${shippingInfo?.unit.getUnitForValue(shippingInfo.frequency)}`;

  const isMobile: boolean = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.md)
  );
  const discountPercent = product.compareAtPrice
    ? Math.floor(
        100 - (price?.asCents() / product.compareAtPrice.asCents()) * 100
      )
    : 0;
  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-lg bg-primary-50",
        responsive && "sm:flex-row sm:flex-wrap md:flex-nowrap"
      )}
      {...attrs}
    >
      <ProductCard
        className={cn(
          "w-full !bg-transparent",
          scentBg &&
            scentBg === "white" &&
            "[&_.product-card-scent-name]:bg-white",
          responsive && "sm:basis-2/3 md:grow-[2] md:basis-0"
        )}
        productCardValue={{
          ...product,
          size: `${quantity} x ${product.size}`,
        }}
        productTitle={
          <h3
            data-id="product-card-title"
            className={cn("title text-sm", responsive && "lg:text-base")}
          >
            {productSelect.name(product)}
          </h3>
        }
        compact={true}
        hoverable={false}
      />
      <div className="flex flex-col px-4 pb-4">
        <div
          className={cn(
            "flex items-center justify-between",
            responsive &&
              "sm:basis-1/3 sm:flex-col sm:items-end sm:justify-center sm:space-x-0 sm:space-y-4 sm:pt-4 md:flex-1 md:flex-row md:items-center md:justify-around md:space-x-4 md:space-y-0"
          )}
        >
          {
            <p className={cn("body flex items-center space-x-1 text-sm")}>
              {shippingInfo ? (
                <>
                  <AutorenewOutlined className="h-4 w-4" />
                  <span className="sr-only">Ships every</span>
                  <span>{combinedFrequency}</span>
                </>
              ) : (
                <>
                  <LocalShippingOutlined className="h-4 w-4" />
                  <span>One-time</span>
                  <span className="sr-only">delivery</span>
                </>
              )}
            </p>
          }
          {product.compareAtPrice ? (
            <PriceDisplay
              showDiscountPrice={true}
              standardPrice={product.compareAtPrice}
              discountPrice={price}
            />
          ) : (
            <PriceDisplay showDiscountPrice={false} standardPrice={price} />
          )}
        </div>
        {discountPercent > 0 && discountPercent !== 100 && (
          <div className="self-end text-xs font-semibold text-secondary-700">
            {`${discountPercent}% OFF`}
          </div>
        )}
      </div>
      {orderItemCardActions && (
        <div className="sm:basis-full md:flex md:basis-44">
          <hr
            className={cn(
              "divider-x my-0",
              responsive && "md:divider-y md:h-full"
            )}
          />
          <div
            className={cn(
              "flex w-full divide-x divide-y-0 divide-solid divide-primary-200",
              responsive &&
                "md:flex-col md:divide-x-0 md:divide-y md:self-stretch"
            )}
          >
            {orderItemCardActions}
          </div>
        </div>
      )}
    </div>
  );
};
